import React, { ReactNode } from "react";
import styled from "styled-components";
import { Avatar as AntAvatar, Tooltip } from "antd";
import { lightGreyOutline, greyText } from "../../styles";

export type IconStackItem = {
  key: string;
  label: string;
  image?: string | null;
};

type IconStackProps = {
  items: IconStackItem[];
  size?: number;
  max?: number;
  children?: ReactNode;
};

const DEFAULT_SIZE = 14;
const DEFAULT_MAX = 3;

const Trigger = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  cursor: default;
`;

const Stack = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
`;

const Mark = styled(AntAvatar)<{ $overlap: number }>`
  flex-shrink: 0;
  border: 1px solid white;
  background-color: ${lightGreyOutline};
  color: ${greyText};
  & + & {
    margin-left: ${({ $overlap }): string => `-${$overlap}px`};
  }
`;

const More = styled.span<{ $size: number }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: ${({ $size }): string => `${$size}px`};
  height: ${({ $size }): string => `${$size}px`};
  margin-left: 2px;
  padding: 0 3px;
  border-radius: 999px;
  background-color: ${lightGreyOutline};
  font-family: "ApercuMono", "Apercu", monospace;
  font-size: 9px;
  color: ${greyText};
`;

const List = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const ListRow = styled.li`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ItemMark = ({
  item,
  size,
}: {
  item: IconStackItem;
  size: number;
}): JSX.Element => (
  <Mark
    size={size}
    src={item.image ?? undefined}
    alt=""
    $overlap={Math.round(size / 3)}
    style={{ fontSize: Math.round(size / 2) }}
  >
    {item.label.charAt(0).toUpperCase()}
  </Mark>
);

export const IconStack = ({
  items,
  size = DEFAULT_SIZE,
  max = DEFAULT_MAX,
  children,
}: IconStackProps): JSX.Element => {
  const visible = items.slice(0, max);
  const hiddenCount = items.length - visible.length;
  return (
    <Tooltip
      placement="top"
      title={
        <List>
          {items.map((item) => (
            <ListRow key={item.key}>
              <ItemMark item={item} size={size} />
              {item.label}
            </ListRow>
          ))}
        </List>
      }
    >
      <Trigger>
        <Stack>
          {visible.map((item) => (
            <ItemMark key={item.key} item={item} size={size} />
          ))}
          {hiddenCount > 0 && <More $size={size}>+{hiddenCount}</More>}
        </Stack>
        {children}
      </Trigger>
    </Tooltip>
  );
};
